import React, { useContext, useEffect, useRef, useState } from "react";
import {
  getOrderItemShutterConfig,
  updateOrderItemShutterConfig,
  resetOrderItemShutterConfigDimensions,
  getRenderMetaData,
} from "../product/service";
import { CoreContainer, Notification } from "../common/style";
import { isBrowser } from "react-device-detect";
import {
  Table,
  Button,
  Spinner,
  Input,
  FormFeedback,
  FormGroup,
  Label,
} from "reactstrap";
import { Icon } from "../common/style";
import { AppContext } from "../context";

const ShutterConfig = ({
  orderItemId,
  projectId,
  groupId,
  itemShutterConfig = null,
  parentCallback,
}) => {
  const [shutterConfig, setShutterConfig] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [updateObj, setUpdateObj] = useState({});
  const [currentEdit, setCurrentEdit] = useState({});
  const [markDirectionInvalid, setMarkDirectionInvalid] = useState(false);
  const [markValueInvalid, setMarkValueInvalid] = useState(false);

  const canvasRef = useRef(null);
  const [renderMetadata, setRenderMetadata] = useState({});
  const [showShutters, setShowShutters] = useState(true);
  const [selectedShutter, setSelectedShutter] = useState(undefined);
  const { orderContext } = useContext(AppContext);

  const maxHeight = 450;
  const maxWidth = 450;

  const directionOptionHash = {
    TOP: "Shrink/Grow from top ",
    BOTTOM: "Shrink/Grow from bottom",
    LEFT: "Shrink/Grow from Left",
    RIGHT: "Shrink/Grow from Right",
  };

  useEffect(() => {
    const element = canvasRef?.current;

    if (!element) return;

    const observer = new ResizeObserver(() => {
      if (element.offsetHeight > maxHeight) {
        window.requestAnimationFrame(() => {
          let updatedWidth = Math.min(
            maxHeight / (renderMetadata.height / renderMetadata.width),
            maxWidth
          );
          element.style.width = `${updatedWidth}px`;
        });
      }

      if (
        element.offsetHeight !==
        element.offsetWidth * (renderMetadata.height / renderMetadata.width)
      ) {
        // element.style.height = `${element.offsetWidth*2}px`
        window.requestAnimationFrame(() => {
          element.style.height = `${
            element.offsetWidth * (renderMetadata.height / renderMetadata.width)
          }px`;
        });
      }
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  });

  const renderAnnotations = (array, attrs = {}) => {
    {
      return array.map((renderObj, index) => {
        let renderStyle = {
          position: "absolute",
          border: `1px solid ${attrs.borderColour || "black"}`,
          height: `${renderObj.height * 100}%`,
          width: `${renderObj.width * 100}%`,
          bottom: `${renderObj.position_y * 100}%`,
          left: `${renderObj.position_x * 100}%`,
          alignContent: "center",
          textAlign: "center",
          opacity: 1,
          hover: { backgroundColor: `${attrs.bgColour || "blue"}` },
        };
        return (
          <span
            style={renderStyle}
            className={`annotation fs-ubuntu ${
              selectedShutter === renderObj.label ? "annotation-selected" : ""
            }`}
            key={index}
            onClick={() => {
              selectedShutter == renderObj.label
                ? setSelectedShutter(undefined)
                : setSelectedShutter(renderObj.label);
            }}
          >
            {renderObj.label}
          </span>
        );
      });
    }
  };

  const renderLayer = (array, attrs = {}) => {
    {
      return array.map((renderObj, index) => {
        let renderStyle = {
          position: "absolute",
          border: `1px solid ${attrs.borderColour || "black"}`,
          height: `${renderObj.height * 100}%`,
          width: `${renderObj.width * 100}%`,
          bottom: `${renderObj.position_y * 100}%`,
          left: `${renderObj.position_x * 100}%`,
          backgroundColor: `${attrs.bgColour || "white"}`,
          alignContent: "center",
          textAlign: "center",
        };
        return (
          <span
            style={renderStyle}
            className={renderObj.filled ? "pattern" : ""}
            key={index}
          ></span>
        );
      });
    }
  };

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  useEffect(() => {
    if (!itemShutterConfig) {
      getOrderItemShutterConfig({
        orderItemId,
        projectId,
        groupId,
      })
        .then((newOrderItem) => {
          setShutterConfig(newOrderItem.customized_item.shutter_size_configs);

          setRenderMetadata(newOrderItem.render_metadata);
          parentCallback(newOrderItem);
        })
        .catch(({ error }) => {
          //TODO: Handle Error
        })
        .finally(() => {
          let defaultCurrentEdit = {
            dimension: undefined,
            shutterIdentifier: undefined,
          };
          setCurrentEdit(defaultCurrentEdit);
          setIsLoading(false);
        });
    } else {
      setShutterConfig(itemShutterConfig);
      setIsLoading(false);
    }
  }, []);

  const changeEditBox = ({ dimension, shutterIdentifier }) => {
    setUpdateObj({});
    setMarkDirectionInvalid(false);
    setMarkValueInvalid(false);

    let newCurrentEdit = {
      dimension: dimension,
      shutterIdentifier: shutterIdentifier,
    };
    setCurrentEdit(newCurrentEdit);
  };

  const handleUpdateObj = ({ key, value, shutterIdentifier }) => {
    if (shutterIdentifier !== updateObj["identifier"]) {
      updateObj["identifier"] = shutterIdentifier;
    }
    updateObj[key] = value;
    setUpdateObj(updateObj);
  };

  const handleDimensionChange = () => {
    setMarkValueInvalid(!updateObj["value"]);
    setMarkDirectionInvalid(!updateObj["direction"]);
    if (!updateObj["value"] || !updateObj["direction"]) {
      return;
    }
    setIsLoading(true);
    updateOrderItemShutterConfig({
      orderItemId,
      projectId,
      groupId,
      updateObj,
    })
      .then((newOrderItem) => {
        setShutterConfig(newOrderItem.customized_item.shutter_size_configs);
        setRenderMetadata(newOrderItem.render_metadata);
        parentCallback(newOrderItem);
      })
      .finally(() => {
        changeEditBox({ dimension: undefined, spaceIdentifier: undefined });
        setIsLoading(false);
      });
  };

  const resetToDefault = () => {
    setIsLoading(true);
    resetOrderItemShutterConfigDimensions({
      orderItemId,
      projectId,
      groupId,
    })
      .then((newOrderItem) => {
        setShutterConfig(newOrderItem.customized_item.shutter_size_configs);
        setRenderMetadata(newOrderItem.render_metadata);
        parentCallback(newOrderItem);
      })
      .finally(() => {
        changeEditBox({ dimension: undefined, spaceIdentifier: undefined });
        setIsLoading(false);
      });
  };

  const dimensionEditBox = ({ dimension_name, shutterIdentifier }) => {
    return (
      <div className="w-100 d-flex  flex row mb-2 bg-light-grey">
        <div className="w-100 bg-medium-grey text-white fs-5 text-start ps-5 fw-bold py-1">
          Change {dimension_name}
        </div>
        <div className="w-100 pt-2">
          <div className="d-flex flex-column align-items-start">
            <div className="fs-6 w-25 text-secondary">New {dimension_name}</div>
            <Input
              id="label"
              name="label"
              type="number"
              placeholder={
                shutterConfig[shutterIdentifier].dimensions[dimension_name]
              }
              className="me-3 w-25"
              invalid={markValueInvalid}
              onChange={(e) => {
                handleUpdateObj({
                  key: "value",
                  value: e.target.value,
                  shutterIdentifier: shutterIdentifier,
                });
              }}
            />
            <FormFeedback className="w-25">Dimension must be set</FormFeedback>
          </div>
        </div>

        <div className="d-flex flex-column col-md align-items-start pt-2 fs-ubuntu">
          <FormGroup
            tag="fieldset"
            onClick={(e) => {
              handleUpdateObj({
                key: "direction",
                value: e.target.value,
                shutterIdentifier: shutterIdentifier,
              });
            }}
          >
            {(dimension_name === "height"
              ? ["TOP", "BOTTOM"]
              : ["LEFT", "RIGHT"]
            ).map((direction, index) => (
              <FormGroup
                check
                key={`${shutterIdentifier}_${index}`}
                className="d-flex align-items-center py-1"
              >
                <Input
                  name="direction"
                  value={direction}
                  type="radio"
                  invalid={markDirectionInvalid}
                  className="me-2"
                />
                <Label check className="px-2">
                  {directionOptionHash[direction]}
                </Label>
                <img
                  src={`/${direction.toLowerCase()}.png`}
                  height="50"
                  className="ps-3"
                ></img>
              </FormGroup>
            ))}
            {markDirectionInvalid && (
              <span className="text-danger fw-normal fs-13px">
                Direction must be selected!
              </span>
            )}
          </FormGroup>
        </div>

        <div className="py-2 text-start px-2">
          <Button
            className="text-end pointer bg-success border-0 fs-6 fw-bold px-4 py-2 mx-1"
            onClick={() => {
              handleDimensionChange();
            }}
          >
            Update {dimension_name}
          </Button>
          <Button
            className="text-start pointer bg-light-subtle text-dark border fs-6 fw-bold px-4 py-2 mx-1"
            onClick={() => {
              changeEditBox({
                dimension: undefined,
                shutterIdentifier: undefined,
              });
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  return isLoading ? (
    <CoreContainer height={isBrowser ? "70vh" : "50vh"}>
      <Spinner size="sm" style={{ margin: "auto" }}>
        Loading...
      </Spinner>
    </CoreContainer>
  ) : Object.keys(shutterConfig).length === 0 ? (
    <div className="d-flex justify-content-center fs-5 py-3">
      Externals Customization is not available for this product.
    </div>
  ) : (
    <div className="d-flex flex-row mt-4">
      <div
        className="position-relative me-7 pt-2"
        style={{ minWidth: `200px` }}
      >
        {renderMetadata && Object.keys(renderMetadata).length !== 0 && (
          <div ref={canvasRef} style={{ position: "relative" }}>
            {renderLayer(renderMetadata.carcass_panels, { bgColour: "black" })}
            {renderLayer(renderMetadata.spaces)}
            {renderLayer(renderMetadata.partitions, {
              bgColour: "saddlebrown",
            })}
            {/*{(!showShutters) && renderAnnotations(renderMetadata.spaces)}*/}

            {showShutters && renderLayer(renderMetadata.shutters)}
            {showShutters && renderAnnotations(renderMetadata.shutters)}
          </div>
        )}
      </div>
      <div className="w-40">
        {notification.type && (
          <Notification
            color={notification.type === "error" ? "danger" : "success"}
            isOpen={isNotificationVisible}
            toggle={onDismissNotification}
          >
            {notification.message}
          </Notification>
        )}

        <div className="w-100 d-flex justify-content-end align-items-center mb-3">
          <Button
            outline
            className=""
            color="secondary"
            size="sm"
            onClick={() => {
              setShowShutters(!showShutters);
            }}
          >
            Toggle Shutters
          </Button>
          <Button
            disabled={orderContext.includes("shutter")}
            outline
            className="ms-1"
            color="secondary"
            size="sm"
            onClick={() => {
              resetToDefault();
            }}
          >
            <span className="fas fa-redo fs-6"></span>
            <span className="ps-1">Reset</span>
          </Button>
        </div>
        <div className="w-100 text-center">
          {Object.keys(shutterConfig).map((shutterIdentifier, index) => {
            return (
              <div key={index}>
                <div
                  className={`w-100 d-flex align-items-center justify-content-center ${
                    selectedShutter === shutterIdentifier
                      ? "selected-row"
                      : "bg-light"
                  }`}
                >
                  <div className="annotation-label">{shutterIdentifier}</div>
                  <div className="col-9 d-flex">
                    <div className="fs-6 p-2 justify-content-center ps-4 text-start col-6">
                      <span className="text-secondary me-2">H</span>
                      <span>
                        {shutterConfig[shutterIdentifier].dimensions.height}
                      </span>
                      {!orderContext.includes("shutter") &&
                        selectedShutter === shutterIdentifier && (
                          <span className="ps-3">
                            <Icon
                              className="fas fa-pencil-alt pointer"
                              size="15px"
                              onClick={() => {
                                changeEditBox({
                                  dimension: "height",
                                  shutterIdentifier: shutterIdentifier,
                                });
                              }}
                            />
                          </span>
                        )}
                    </div>
                    <div className="fs-6 p-2 ps-2 text-start col-6">
                      <span className="text-secondary me-2">W</span>
                      <span>
                        {shutterConfig[shutterIdentifier].dimensions.width}
                      </span>
                      {!orderContext.includes("shutter") &&
                        selectedShutter === shutterIdentifier && (
                          <span className="ps-3">
                            <Icon
                              className="fas fa-pencil-alt pointer"
                              size="15px"
                              onClick={() => {
                                changeEditBox({
                                  dimension: "width",
                                  shutterIdentifier: shutterIdentifier,
                                });
                              }}
                            />
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="col-2 fs-5">
                    {/*<div onClick={() =>*/}
                    {/*{}*/}
                    {/*}>*/}
                    {/*  <span className="fas fa-plus-circle pointer fs-6" ></span>*/}
                    {/*  <span className="fs-6 ps-2 link pointer">Color</span>*/}
                    {/*</div>*/}
                  </div>
                </div>
                {currentEdit.dimension &&
                  currentEdit.shutterIdentifier === shutterIdentifier &&
                  dimensionEditBox({
                    dimension_name: currentEdit.dimension,
                    shutterIdentifier: shutterIdentifier,
                  })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ShutterConfig;
